import { Component, computed, inject, input, linkedSignal, signal } from '@angular/core';
import { DokaButton, DokaModal, DokaModalController } from '@doka/angular-core';
import { I18NextEagerPipe, I18NextService } from 'angular-i18next';
import { firstValueFrom } from 'rxjs';
import {
  IFiscalYearCountryModel,
  NotifyHeadsOfCountryModel,
  PlansClient,
} from 'src/api/services/generated/api-clients';
import {
  CountryCheckboxListComponent,
  CountryCheckboxOption,
} from 'src/app/shared/components/country-checkbox-list/country-checkbox-list.component';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import { ToastService } from 'src/app/shared/services/toast.service';

@Component({
  templateUrl: './notify-heads-of-country-dialog.component.html',
  imports: [DokaModal, DokaButton, I18NextEagerPipe, SpinnerComponent, CountryCheckboxListComponent],
})
export class NotifyHeadsOfCountryDialogComponent {
  private readonly modalCtrl = inject(DokaModalController);
  private readonly plansClient = inject(PlansClient);
  private readonly toastService = inject(ToastService);
  private readonly i18n = inject(I18NextService);

  public readonly fiscalYearId = input.required<string>();
  public readonly year = input.required<number>();
  /** Countries that have plans in the fiscal year. */
  public readonly countries = input.required<IFiscalYearCountryModel[]>();

  protected readonly isLoading = signal(false);

  protected readonly countryOptions = computed<CountryCheckboxOption[]>(() =>
    this.countries().map((country) => ({
      id: country.countryId,
      label: country.name,
      disabled: !country.headOfCountryCount,
      description: this.describe(country),
    })),
  );

  // Countries that were already notified stay unselected so a second click does not spam them.
  protected readonly selectedCountryIds = linkedSignal<ReadonlySet<string>>(
    () =>
      new Set(
        this.countries()
          .filter((country) => country.headOfCountryCount && !country.lastNotifiedAt)
          .map((country) => country.countryId),
      ),
  );

  protected closeModal(): void {
    this.modalCtrl.dismiss();
  }

  protected async submit() {
    this.isLoading.set(true);
    try {
      const result = await firstValueFrom(
        this.plansClient.notifyHeadsOfCountry(
          this.fiscalYearId(),
          new NotifyHeadsOfCountryModel({ countryIds: [...this.selectedCountryIds()] }),
        ),
      );
      this.modalCtrl.dismiss(result, 'sent');
    } catch (error) {
      console.error('Error notifying heads of country', error);
      await this.toastService.showError(this.i18n.t('MasterData.PlansGrid.NotifyHeadsOfCountryError'));
    } finally {
      this.isLoading.set(false);
    }
  }

  private describe(country: IFiscalYearCountryModel): string {
    if (!country.headOfCountryCount) {
      return this.i18n.t('MasterData.PlansGrid.NoHeadOfCountry');
    }

    if (!country.lastNotifiedAt) {
      return this.i18n.t('MasterData.PlansGrid.NotNotifiedYet');
    }

    // Angular escapes the rendered text, i18next must not escape the slashes of the date again.
    return this.i18n.t('MasterData.PlansGrid.LastNotifiedOn', {
      date: this.formatDate(country.lastNotifiedAt),
      interpolation: { escapeValue: false },
    });
  }

  private formatDate(date: Date): string {
    return date.toLocaleString(this.i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
